import { useTranslation } from 'react-i18next';
import React, { useMemo, useCallback, useRef } from 'react';
import { useForm, useFieldArray, Controller, useWatch } from 'react-hook-form';
import cx from 'clsx';
import { QueryClientProvider } from 'react-query';

import { IconRcAdd } from '@/assets/common';
import { EligibilitiesFields, LabelInput } from '@/components';
import { Channel, RichTextEditor, RichTextEditorHandle } from '@/components/RichTextEditor';
import { useCommentEligibility, useSwitch, useChannelPick } from '@/hooks';
import { devLog, queryClient } from '@/utils';
import { SUPPORTED_LOCALE_TYPE } from '@/locales';

import { PostFooter } from '../PostFooter';
import { EndTime, POLL_MIN_DURATION } from './EndTime';
import style from './index.module.scss';

type PollProps = {
  onConfirm: (data: EditorSubmitContentData) => Promise<void>;
  disabled?: boolean;
  className?: string;
  language?: SUPPORTED_LOCALE_TYPE;
  refreshKey?: string;
  hiddenEligibilityFields?: string[];
  channel?: Channel;
  hideChannelPicker?: boolean;
};

export const Poll = ({
  onConfirm,
  disabled = false,
  className,
  language,
  refreshKey,
  hiddenEligibilityFields,
  channel,
  hideChannelPicker,
}: PollProps) => {
  const { t } = useTranslation();

  const {
    handleSubmit,
    control,
    formState: { isValid },
    setValue,
  } = useForm({
    mode: 'onChange',
    defaultValues: {
      duration: 24 * 3600,
      // poll_data.permissions
      permissions: {
        has_web3_id: true,
      },
      options: [{ value: undefined }, { value: undefined }],
    } as any,
  });
  const { on: isLoading, turn: setLoading } = useSwitch();
  const { on: isEditorInValid, turn: setInvalid } = useSwitch();
  const editorRef = useRef<RichTextEditorHandle>(null);
  const { channelId } = useChannelPick(channel);

  const [time, options] = useWatch({ control, name: ['time', 'options'] });

  const duration = useMemo(() => {
    if (!time?.day && !time?.hour && !time?.minute) {
      return 0;
    }

    return (
      (Number(time?.day) || 0) * 24 * 60 * 60 +
      (Number(time?.hour) || 0) * 60 * 60 +
      (Number(time?.minute) || 0) * 60
    );
  }, [time]);

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'options',
  });

  const [commentEligibility] = useCommentEligibility();

  const onSubmit = useCallback(
    async data => {
      try {
        setLoading(true);
        const params: EditorSubmitContentData = {
          type: 'poll',
          content: '',
          permissions: commentEligibility,
          channel_id: channelId,
        };

        params.poll_data = {
          options: data.options.map(x => x.value).filter(x => !!x),
          duration: duration,
          permissions: Object.keys(data.permissions).reduce((m, n) => {
            if (Array.isArray(data.permissions[n])) {
              // 这里注意，处理的很糙，选中且输入有值
              if (data.permissions[n][0] && data.permissions[n][1]) {
                const num = Number(data.permissions[n][1]);
                m[n] = isNaN(num) ? data.permissions[n][1] : num;
              }
            } else if (data.permissions[n]) {
              m[n] = true;
            }
            return m;
          }, {} as any),
        };

        const textData = await editorRef.current?.getSubmitContent();

        if (textData) {
          Object.assign(params, {
            content: textData.textContent,
            entities: {
              ...(textData.mentionsList?.length ? { mentions: textData.mentionsList } : {}),
              ...(textData.hashtagsList?.length ? { tags: textData.hashtagsList } : {}),
              ...(textData.urlsWidthIndices?.length ? { url: textData.urlsWidthIndices } : {}),
              ...(textData.slashesList.length ? { channels: textData.slashesList } : {}),
            },
          });
        }

        devLog('submit draw content', params);
        await onConfirm?.(params);
      } catch (error) {
        setLoading(false);
      }
    },
    [setLoading, commentEligibility, duration, onConfirm, channelId],
  );

  const optionValid = useMemo(() => options?.filter(x => !!x.value).length >= 2, [options]);

  return (
    <QueryClientProvider client={queryClient}>
      <div className={cx(style.container, className)}>
        <div className={style.main}>
          <div className={style.title}>{t('Poll.1th4awr')}</div>
          <RichTextEditor
            maxLength={600}
            editorClassName={style.textEditor}
            placeholderClassName={style.editorStyle}
            wrapperClassName={style.editorWrapper}
            toolbar={false}
            footer={false}
            disableUploadImage
            placeholder={t('Poll.x9tvat')}
            disabled={disabled}
            onInvalidChange={setInvalid}
            disableCard
            ref={editorRef}
            language={language}
          />
          <div className={style.choices}>
            <div>
              {fields.map((field, index) => (
                <Controller
                  key={index + field.id}
                  name={`options.${index}.value`}
                  control={control}
                  render={({ field: f }) => (
                    <div className={style.choice}>
                      <LabelInput
                        ref={f.ref}
                        value={f.value}
                        onChange={f.onChange}
                        maxLength={25}
                        disabled={disabled}
                        label={t('Poll.choice', { choice: index + 1 })}
                        className={style.input}
                        onRemoveClick={() => remove(index)}
                        removeable={fields.length > 2}
                      />
                    </div>
                  )}
                />
              ))}
            </div>
            {fields.length < 4 && !disabled ? (
              <IconRcAdd className={style.addIcon} onClick={() => append({ value: undefined })} />
            ) : null}
          </div>

          <div className={style.subTitle}>{t('Poll.crlyrr')}</div>
          <EndTime control={control} duration={duration} setValue={setValue} disabled={disabled} />
          <EligibilitiesFields
            control={control}
            disabled={disabled}
            key={refreshKey}
            hiddenEligibilityFields={hiddenEligibilityFields}
          />
        </div>
        <PostFooter
          disabled={
            disabled ||
            isEditorInValid ||
            !isValid ||
            !duration ||
            duration < POLL_MIN_DURATION ||
            !optionValid
          }
          isLoading={isLoading}
          onSubmit={handleSubmit(onSubmit)}
          hiddenEligibilityFields={hiddenEligibilityFields}
          hideChannelPicker={hideChannelPicker}
        />
      </div>
    </QueryClientProvider>
  );
};
